import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@app/app.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserLevel } from '@app/core/models/user-level';
import { ActiveLevels } from '@app/menu/components/breadcrumbs/active-levels';
import { BreadcrumbRoute } from '@app/menu/components/breadcrumbs/breadcrumb-route';
import { RouteData } from '@app/menu/components/breadcrumbs/route-data';
import { MenuService } from '@app/menu/services/menu.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  standalone: false,
})
export class BreadcrumbsComponent implements OnInit {
  @ViewChild('breadcrumb', { read: ElementRef, static: true })
  breadcrumb: ElementRef<HTMLOListElement>;

  menuData$ = this.menuService.menuData$.pipe(untilDestroyed(this));

  activeLevels: ActiveLevels = {
    isEndUser: false,
    isEnterprise: false,
    isServiceProvider: false,
    isSystemAdmin: false,
    menuLevelChanged: false,
  };

  routeData: RouteData = {
    current: undefined,
    last: undefined,
  };

  currentRoute?: BreadcrumbRoute;
  userIsSystemAdmin: boolean = false;

  private contextActiveClass = 'breadcrumb-context--active';

  public UserLevel = UserLevel;

  constructor(
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private authenticationService: AuthenticationService,
    private menuService: MenuService
  ) {}

  ngOnInit(): void {
    this.authenticationService.authUser$.pipe(untilDestroyed(this)).subscribe(() => {
      this.userIsSystemAdmin = this.authenticationService.userIsSystemAdmin();
    });

    this.appService.initialized$.subscribe(() => {
      this.menuService.menuData$.pipe(untilDestroyed(this)).subscribe((menuData) => {
        this.activeLevels = menuData.activeUserLevels;

        this.routeData.last = this.routeData.current;
        this.routeData.current = {
          name: `route.${menuData.routeName}`,
          params: menuData.routeParams,
        };
      });

      this.menuService.genericBreadcrumb$.pipe(untilDestroyed(this)).subscribe((value) => {
        if (value === 'will-change') {
          this.currentRoute = this.routeData.last;
        } else if (value === undefined) {
          this.currentRoute = this.routeData.current;
        } else {
          this.currentRoute = this.routeData.current;
          this.currentRoute.genericBreadcrumb = value;
        }
      });
    });

    this.observeChanges();
  }

  /**
   * Mark the last child with the class .breadcrumb-context as active.
   */
  private markContextAsActive() {
    const children = this.breadcrumb.nativeElement.querySelectorAll('.breadcrumb-context');

    if (children.length > 0) {
      children.forEach((child) => child.classList.remove(this.contextActiveClass));
      children.item(children.length - 1).classList.add(this.contextActiveClass);
    }
  }

  /**
   * Observe changes to the children of the breadcrumb root (<ol>...</ol>). Whenever the children
   * change we search for the last child with the class .breadcrumb-context and mark it as active.
   */
  private observeChanges() {
    const mutationObserver = new MutationObserver(() => this.markContextAsActive());

    mutationObserver.observe(this.breadcrumb.nativeElement, {
      childList: true,
    });
  }
}
